import dynamic from "next/dynamic";
import Image from "next/image";
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";

const HeroContactFormClient = dynamic(
  () => import("../SecondaryHero/HeroContactFormClient"),
  { ssr: false }
);

interface WideHeroProps {
  data?: {
    right_layout?: boolean;
    heading?: {
      text?: string;
      title?: string;
    };
    text?: {
      text?: string;
    };
    button?: {
      label?: string;
      href?: string;
    };
    desktop_bg_image?: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
    mobile_bg_image?: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  };
}

export const fields = [
  "right_layout",
  "heading.text",
  "heading.title",
  "text.text",
  "button.label",
  "button.href",
  "desktop_bg_image.alt",
  "desktop_bg_image.image_file.image.id",
  "desktop_bg_image.image_file.image.filename_download",
  "mobile_bg_image.alt",
  "mobile_bg_image.image_file.image.id",
  "mobile_bg_image.image_file.image.filename_download",
] as const;

export default function WideHero({ data }: WideHeroProps) {
  const desktopImage = data?.desktop_bg_image?.image_file?.image?.id
    ? getReadableImageUrl(
        data.desktop_bg_image?.image_file?.image?.id || "",
        data.desktop_bg_image?.image_file?.image?.filename_download || ""
      )
    : "/images/wall-with-logo-desktop.png";
  const mobileImage = data?.mobile_bg_image?.image_file?.image?.id
    ? getReadableImageUrl(
        data.mobile_bg_image?.image_file?.image?.id || "",
        data.mobile_bg_image?.image_file?.image?.filename_download || ""
      )
    : "/images/wall-with-logo-mobile.png";

  return (
    <section className="relative h-[49.3125rem] md:h-[53.25rem] bg-lightGray overflow-hidden">
      <HeroContactFormClient />
      <div className="absolute inset-0">
        {data?.desktop_bg_image && (
          <Image
            src={desktopImage}
            alt={data?.desktop_bg_image?.alt}
            fill
            className="object-cover object-left md:block hidden w-full"
            sizes="(max-width: 768px) 0vw, 100vw"
            loading="lazy"
            quality={65}
          />
        )}
        {data?.mobile_bg_image && (
          <Image
            src={mobileImage}
            alt={data?.mobile_bg_image?.alt}
            fill
            className="object-cover md:hidden block"
            sizes="(max-width: 768px) 100vw, 0vw"
            priority
            fetchPriority="high"
            quality={65}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-90% to-transparent xl:bg-gradient-to-r xl:from-white/90 xl:to-60% xl:to-transparent md:hidden" />
      </div>
      <div className="relative w-full h-full px-8 lg:px-16">
        <div className="h-full flex flex-col justify-end pb-8 md:pb-32">
          <div
            className={`md:bg-white/65 md:backdrop-blur-sm md:rounded-xl p-0 md:p-12 max-w-3xl ${
              data?.right_layout ? "md:ml-auto" : ""
            }`}
          >
            <div className="space-y-8 md:space-y-12">
              <h1 className="text-[2.5rem] font-essonnes font-light leading-tight text-white md:text-[3.625rem] lg:text-[4rem] md:text-primary">
                {data?.heading?.title}
              </h1>
              <div
                className="text-white md:text-primary font-greycliff text-[1rem] font-normal leading-normal md:text-xl lg:text-2xl md:font-medium md:leading-7 prose prose-p:m-0 [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl md:[&>*]:font-medium md:[&>*]:leading-7"
                dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
              />
              <Link
                href={data?.button?.href || ""}
                className="inline-block bg-light md:bg-primary text-primary md:text-white px-9 py-4 rounded-[3.125rem] hover:bg-light hover:text-primary transition-all duration-200 font-greycliff text-xl w-full md:w-auto shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] text-center"
              >
                {data?.button?.label}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
